import type {
    ICategory,
    IInfluencer,
    IInfluencerPastWork,
    IInfluencerPublicProject,
    IPlatformStats,
} from '../../redux/buzzbridge/types';
import type { IProject } from '../../types/global/helper';
import { Status } from '../../types/global/generic.types';
import {
    PLATFORM_DELIVERABLES,
    type RateCardPlatformId,
    type PriceCardContentKey,
    getStoredDeliverableRate,
} from '../BuzzBridge/utils/rateCardPlatformConfig';
import { engagementFromInfluencer } from '../BuzzBridge/utils/creatorProfileHelpers';
import { formatSocialHandle } from '../../utils/socialHandle';
import { resolveInfluencerPlatformProfileUrl } from '../../utils/influencerPlatformProfileUrl';

/** Shown when an influencer has no category set. */
export const DEFAULT_CREATOR_CATEGORY = 'General Creator';

/** First category label, or {@link DEFAULT_CREATOR_CATEGORY} when missing. */
export function creatorPrimaryCategoryLabel(categories?: Array<string | ICategory | null | undefined> | null): string {
    if (!Array.isArray(categories) || categories.length === 0) return DEFAULT_CREATOR_CATEGORY;
    for (const c of categories) {
        if (typeof c === 'string') {
            const t = c.trim();
            if (t) return t;
            continue;
        }
        if (c && typeof c === 'object') {
            const main = typeof c.main === 'string' ? c.main.trim() : '';
            if (main) return main;
            const sub = typeof c.sub === 'string' ? c.sub.trim() : '';
            if (sub) return sub;
        }
    }
    return DEFAULT_CREATOR_CATEGORY;
}

export function getInfluencerAccountUserName(influencer: IInfluencer): string | undefined {
    const cb = influencer.created_by;
    if (cb && typeof cb === 'object' && 'userName' in cb && cb.userName) {
        return cb.userName;
    }
    if (influencer.userName) return influencer.userName;
    return undefined;
}

export function formatReachShort(n?: number): string {
    if (n == null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toLocaleString();
}

export function formatUsd(amount: number): string {
    if (amount <= 0) return '—';
    return `$${amount.toLocaleString()}`;
}

/**
 * Prefer list/API avg_engagement_rate — already a percentage (e.g. 0.3 → "0.3%").
 * Do not ×100; that double-scales values under 1% vs Find Talent cards.
 * Fall back to platform rates (stored as decimals) via engagementFromInfluencer.
 */
export function publicEngagementLabel(influencer: IInfluencer): string {
    const avg = influencer.avg_engagement_rate;
    if (avg != null && !Number.isNaN(Number(avg))) {
        return `${Number(avg).toFixed(1)}%`;
    }
    return engagementFromInfluencer(influencer).label;
}

export function publicTotalReach(influencer: IInfluencer): number | undefined {
    if (influencer.totalReach && influencer.totalReach > 0) return influencer.totalReach;
    const sum =
        (influencer.instagram?.followers ?? 0) +
        (influencer.tiktok?.followers ?? 0) +
        (influencer.youtube?.followers ?? 0);
    return sum > 0 ? sum : undefined;
}

export function isAbsoluteMediaUrl(url?: string): url is string {
    return !!url && /^https?:\/\//i.test(url.trim());
}

/** Hero cover — only absolute URLs; prefers profile picture over relative social_thumbnail paths */
export function heroImageUrl(influencer: IInfluencer): string | undefined {
    const candidates = [
        influencer.profile_picture,
        influencer.instagram?.profile_picture,
        influencer.tiktok?.profile_picture,
        influencer.youtube?.profile_picture,
        typeof influencer.created_by === 'object' ? influencer.created_by.profile_picture : undefined,
        isAbsoluteMediaUrl(influencer.social_thumbnail) ? influencer.social_thumbnail : undefined,
    ];
    return candidates.find(isAbsoluteMediaUrl);
}

export function mapPublicProjectToIProject(project: IInfluencerPublicProject): IProject {
    const id = project._id ?? '';
    return {
        _id: id,
        id,
        title: project.title ?? 'Untitled',
        description: project.description ?? '',
        feature_image: project.feature_image ?? '',
        project_type: project.project_type ?? 'video',
        project_gallery: project.project_gallery ?? [],
        visible: project.visible ?? true,
        team: [],
        status: (project.status as Status) ?? 'published',
        trending_marker: 0,
        project: id,
        video_id: project.video_id,
        hls: project.hls,
        thumbnail: project.thumbnail,
        media_type: (project.media_type as IProject['media_type']) ?? 'video',
        processing_complete: project.processing_complete ?? Boolean(project.hls || project.video_id),
        downloadable: project.downloadable,
        resolutions: project.resolutions as IProject['resolutions'],
        download_link: project.download_link,
        public_status: project.public_status as IProject['public_status'],
        createdAt: project.createdAt,
    };
}

export interface PortfolioItem {
    id: string;
    title: string;
    subtitle?: string;
    description?: string;
    imageUrl?: string;
    link?: string;
}

export function publicGalleryUrls(influencer: IInfluencer): string[] {
    return (influencer.gallery ?? []).filter(isAbsoluteMediaUrl);
}

export function publicProjectsFromInfluencer(influencer: IInfluencer): IInfluencerPublicProject[] {
    return influencer.projects ?? [];
}

export type PublicProfileGridItem =
    | { id: string; kind: 'photo'; url: string }
    | { id: string; kind: 'video'; project: IProject; thumbnail: string };

/** Projects (videos) first, then gallery photos — single 4-column grid */
export function publicProfileGridItems(influencer: IInfluencer): PublicProfileGridItem[] {
    const videos: PublicProfileGridItem[] = publicProjectsFromInfluencer(influencer).map(p => {
        const project = mapPublicProjectToIProject(p);
        const thumbnail =
            (isAbsoluteMediaUrl(p.thumbnail) ? p.thumbnail : undefined) ||
            (isAbsoluteMediaUrl(p.feature_image) ? p.feature_image : undefined) ||
            '';
        return { id: `video-${project._id}`, kind: 'video' as const, project, thumbnail };
    });

    const photos: PublicProfileGridItem[] = publicGalleryUrls(influencer).map((url, i) => ({
        id: `photo-${i}`,
        kind: 'photo' as const,
        url,
    }));

    const videoItems = videos.filter(
        (v): v is Extract<PublicProfileGridItem, { kind: 'video' }> => v.kind === 'video' && v.thumbnail !== '',
    );
    return [...videoItems, ...photos];
}

export function portfolioItemsFromInfluencer(influencer: IInfluencer): PortfolioItem[] {
    const fromPast = (influencer.past_work ?? []).map((w: IInfluencerPastWork, i) => ({
        id: w._id ?? `past-${i}`,
        title: w.title ?? 'Untitled',
        subtitle: [w.year, w.type].filter(Boolean).join(' · '),
        description: w.description,
        link: w.link,
    }));

    return fromPast;
}

export interface PlatformRateRow {
    label: string;
    amount: number;
}

export interface PlatformRateCard {
    platform: RateCardPlatformId;
    label: string;
    username?: string;
    followers?: number;
    linked: boolean;
    highlighted?: boolean;
    profileLink?: string;
    rows: PlatformRateRow[];
}

const PLATFORM_ORDER: RateCardPlatformId[] = ['instagram', 'tiktok', 'youtube'];

function platformStats(influencer: IInfluencer, id: RateCardPlatformId): IPlatformStats | undefined {
    return influencer[id];
}

export function platformRateCardsFromInfluencer(influencer: IInfluencer): PlatformRateCard[] {
    const cards: PlatformRateCard[] = [];
    for (const platform of PLATFORM_ORDER) {
        const stats = platformStats(influencer, platform);
        if (!stats?.username && !stats?.followers) continue;

        const deliverables = PLATFORM_DELIVERABLES[platform];
        const rows: PlatformRateRow[] = [];
        for (const d of deliverables) {
            const amount = getStoredDeliverableRate(stats.pricing, d.key as PriceCardContentKey);
            if (amount > 0) {
                rows.push({ label: d.title, amount });
            }
        }
        if (rows.length === 0 && deliverables[0]) {
            const fallback = getStoredDeliverableRate(stats.pricing, deliverables[0].key as PriceCardContentKey);
            if (fallback > 0) rows.push({ label: deliverables[0].title, amount: fallback });
        }

        const label = platform === 'instagram' ? 'Instagram' : platform === 'tiktok' ? 'TikTok' : 'YouTube';

        cards.push({
            platform,
            label,
            username: stats.username,
            followers: stats.followers,
            linked: Boolean(stats.linked ?? stats.username),
            profileLink: resolveInfluencerPlatformProfileUrl(platform, stats),
            rows: rows.slice(0, 3),
        });
    }

    if (cards.length > 0) {
        const maxFollowers = Math.max(...cards.map(c => c.followers ?? 0));
        cards.forEach(c => {
            c.highlighted = (c.followers ?? 0) === maxFollowers && maxFollowers > 0;
        });
    }
    return cards;
}

export function accountHandleLabel(influencer: IInfluencer): string | undefined {
    const userName = getInfluencerAccountUserName(influencer);
    return userName ? formatSocialHandle(userName) : undefined;
}
